import React, { useCallback, useMemo, useState } from 'react';
import SaveAnalysisModal, {
  useKeywordAnalysisSave,
} from '../../component/SaveAnalysisModal';
import { useFilterGptInfo } from '../../hook';
import { PromptConfirmItemType } from '@op/components/GPT/types';
import { DBO_TM } from '@op/utils/format';
import KeywordCard from './KeywordCard';
import Filter from '../../component/Filter';
import { ChangeFilterPropsType, GPTContentType } from '../../types';
import useTranslateTopic from '@op/hooks/useTranslateTopic';
import { useFontLoading } from '@op/hooks/common';
import { InjectionProvider } from '../../provider';
import '../../scss/analysisCard.scss';

interface ICreateAnalysis {
  gameInfo: any;
  wholeGameInfo: any;
  isPreviews?: boolean;
  isCreator?: boolean;
  shortId?: string;
  leadId?: number;
  setApiLoading?: (flag: string) => void;
  backGptInfo?: (list: GPTContentType[]) => void;
  userInfo: any;
  changeTab?: () => void;
  resetAnalysis?: () => Promise<any>;
  dataType?: 'new' | 'legacy';
  versionId?: number;
  isTopicAnalysis?: boolean;
  originalShortUrl?: string;
  isSubscription?: boolean;
  replaceParams?: any;
  successTagged?: boolean;
  title?: string;
  /** 隐藏筛选器底部操作按钮（确定 / 重置 / Stop），用于只读内嵌场景 */
  hideFilterOperateBtn?: boolean;
  /** 全部预览模式，预览模式下筛选器会有特殊样式调整（FilterBox: dbo-filterbox--all-preview） */
  allPreview?: boolean;
}

export default function KeywordCardAnalysis({
  gameInfo,
  wholeGameInfo,
  isPreviews = false,
  shortId,
  isCreator = true,
  setApiLoading,
  userInfo,
  changeTab,
  resetAnalysis,
  leadId,
  dataType = 'new',
  versionId: propsVersionId,
  isTopicAnalysis,
  originalShortUrl,
  isSubscription,
  replaceParams,
  successTagged = false,
  title,
  hideFilterOperateBtn,
  allPreview,
}: ICreateAnalysis) {
  const {
    saveVisible,
    setSaveParams,
    saveUserSummary,
    updateKeywordSummary,
    resetSaveId,
    setSaveId,
    saveTipsElement,
    toggleSaveWindow,
    saveTipsIcon,
    setIsSave,
  } = useKeywordAnalysisSave({ isPreviews, gameInfo, changeTab, isCreator });
  const [loading, setLoading] = useState(isPreviews);
  const { loading: fontLoading, changeLoading } = useFontLoading({
    defaultLoading: true,
  });
  // 这个是整个任务的id
  const [taskId, setTaskId] = useState<number | undefined>(leadId);
  const [versionId, setVersionId] = useState<number | undefined>(
    propsVersionId,
  );
  const { setTranslateTopicMap, translateTopic } = useTranslateTopic();
  const {
    promptInfoMap,
    changePromptInfo,
    promptList,
    videoPromptList,
    oriFilterValue,
    changeFilterValue,
    filterValue,
    setGptList,
  } = useFilterGptInfo({
    gameInfo,
    wholeGameInfo,
    updateSummary: updateKeywordSummary,
    setSaveParams,
    isPreviews,
    defaultTab: 'keyword',
  });

  function generateAnalysisClick({
    filter,
    oriFilter,
    tab,
    pageFilter,
    promptInfo,
    gptList,
    isSave = false,
    isFirst,
    taskId,
    versionId,
  }: ChangeFilterPropsType) {
    changeFilterValue(filter, oriFilter, tab);
    setSaveParams((value: any) => ({
      ...value,
      ...pageFilter,
      data_type: dataType,
    }));
    setGptList(gptList || []);
    setIsSave(isSave);

    if (!isFirst) {
      setTaskId?.(taskId);
      setSaveId?.(taskId);
      setVersionId?.(versionId);
    }
    changeLoading();
    promptInfo && changePromptInfo(promptInfo);
  }

  const getSaveWindowsName = useCallback(() => {
    return DBO_TM.timeTitle(filterValue);
  }, [filterValue]);

  function renderBody() {
    if (fontLoading) return null;

    if (filterValue === null || !taskId) {
      return null;
    }
    return (
      <KeywordCard
        filterValue={filterValue}
        saveTipsElement={saveTipsElement}
        isPreviews={isPreviews}
        promptList={promptList}
        setApiLoading={setApiLoading}
        promptInfo={promptInfoMap.keyword}
        setPromptInfo={(promptInfo: PromptConfirmItemType, _params?: any) => {
          const params: any = isCreator ? _params : null;
          changePromptInfo({ keyword: promptInfo }, params);
        }}
        oriFilterValue={oriFilterValue}
        translateTopic={translateTopic}
      />
    );
  }
  /**
   * 这个主要是给预览筛选器使用
   */
  const contextValue = useMemo(() => {
    return {
      dataType,
      taskId,
      wholeGameInfo,
      gameInfo,
      setLoading,
      isTopicAnalysis,
      originalShortUrl,
      isSubscription,
      versionId,
      replaceParams,
      successTagged,
      title,
      isPreviews,
      hideFilterOperateBtn,
      allPreview,
    };
  }, [
    dataType,
    taskId,
    wholeGameInfo,
    gameInfo,
    setLoading,
    isTopicAnalysis,
    originalShortUrl,
    versionId,
    successTagged,
    title,
    isPreviews,
    hideFilterOperateBtn,
    allPreview,
  ]);

  return (
    <InjectionProvider contextValue={contextValue}>
      <Filter
        changeFilterValue={generateAnalysisClick}
        gameInfo={gameInfo}
        shortId={shortId}
        setLoading={setLoading}
        loading={loading}
        tab="keyword"
        setTranslateTopicMap={setTranslateTopicMap}
        promptList={promptList}
        videoPromptList={videoPromptList}
        resetSaveId={resetSaveId}
        isPreview={isPreviews}
        resetAnalysis={resetAnalysis}
        isCreator={isCreator}
        promptInfoMap={promptInfoMap}
        wholeGameInfo={wholeGameInfo}
        userInfo={userInfo}
        leadId={leadId}
        changeTab={changeTab}
      />

      {renderBody()}

      {/* 浮动的保存图标 */}
      {filterValue !== null && saveTipsIcon}

      {!isPreviews && (
        <SaveAnalysisModal
          visible={saveVisible}
          wholeGameInfo={wholeGameInfo}
          saveUserSummary={saveUserSummary}
          close={() => toggleSaveWindow()}
          getSaveWindowsName={getSaveWindowsName}
        />
      )}
    </InjectionProvider>
  );
}
